import * as React from "react";
import { cn } from "~/lib/utils";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  error?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  clearable?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      error,
      prefix,
      suffix,
      clearable,
      disabled,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    /*
     * If `value` is supplied the field is controlled and renders exactly what
     * it is given. Otherwise it stays a native uncontrolled input so that
     * react-hook-form's `register`/`reset` can write the DOM through the ref.
     * Seeding internal React state from `defaultValue` once made every later
     * `reset()` a no-op — the workspace name stayed blank after bootstrap.
     */
    const isControlled = value !== undefined;
    const innerRef = React.useRef<HTMLInputElement>(null);
    const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue ?? "");

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const displayValue = isControlled ? value : uncontrolledValue;

    const handleClear = () => {
      if (!isControlled && innerRef.current) {
        innerRef.current.value = "";
        setUncontrolledValue("");
      }
      onChange?.({
        target: { value: "" },
        currentTarget: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            "flex items-center gap-2 rounded-sm border transition-colors duration-fast motion-reduce:transition-none focus-within:ring-0 focus-within:shadow-[var(--shadow-focus)]",
            error
              ? "border-error bg-error-soft/20"
              : "border-border bg-background hover:border-border-strong focus-within:border-ring",
            disabled && "opacity-50"
          )}
        >
          {prefix && (
            <div className="pl-3 text-sm text-foreground-muted">{prefix}</div>
          )}
          <input
            type={type}
            className={cn(
              "flex h-10 w-full bg-transparent px-3 py-2 text-sm placeholder:text-foreground-muted focus-visible:outline-none disabled:cursor-not-allowed",
              prefix && "pl-0",
              suffix && "pr-0",
              clearable && "pr-0",
              className
            )}
            ref={setRefs}
            disabled={disabled}
            onChange={(event) => {
              if (!isControlled) setUncontrolledValue(event.target.value);
              onChange?.(event);
            }}
            {...props}
            {...(isControlled ? { value } : { defaultValue })}
          />
          {clearable && displayValue !== "" && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="mr-1 inline-flex size-6 min-h-6 min-w-6 shrink-0 items-center justify-center text-foreground-muted transition-colors hover:text-foreground outline-none focus-visible:shadow-focus"
              aria-label="Clear input"
            >
              ✕
            </button>
          )}
          {suffix && !clearable && (
            <div className="pr-3 text-sm text-foreground-muted">{suffix}</div>
          )}
        </div>
        {error && (
          <p className="text-xs text-error-ink" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
